// FoodHabitsLab.jsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import HealthyImg from "../../../../asstes/habits/food_healthy.jpg";
import UnhealthyImg from "../../../../asstes/habits/food_unhealthy.jpg";
import HygieneImg from "../../../../asstes/habits/food_hygiene.jpg";
import FoodIntroImg from "../../../../asstes/habits/food_intro.jpg"; 



export default function FoodHabitsLab() {
  const { t } = useTranslation("habits");

const FOOD_STEPS = [
  {
    id: "healthy",
    label: t("food.healthy_label"),
    title: t("food.healthy_title"),
    description: t("food.healthy_desc"),
    items: [
      t("food.healthy_items_1"),
      t("food.healthy_items_2"),
      t("food.healthy_items_3"),
      t("food.healthy_items_4"),
    ],
    example: t("food.healthy_example"),
  },
  {
    id: "unhealthy",
    label: t("food.unhealthy_label"),
    title: t("food.unhealthy_title"),
    description: t("food.unhealthy_desc"),
    items: [
      t("food.unhealthy_items_1"),
      t("food.unhealthy_items_2"),
      t("food.unhealthy_items_3"),
      t("food.unhealthy_items_4"),
    ],
    example: t("food.unhealthy_example"),
  },
  {
    id: "hygiene",
    label: t("food.hygiene_label"),
    title: t("food.hygiene_title"),
    description: t("food.hygiene_desc"),
    items: [
      t("food.hygiene_items_1"),
      t("food.hygiene_items_2"),
      t("food.hygiene_items_3"),
      t("food.hygiene_items_4"),
    ],
    example: t("food.hygiene_example"),
  },
];

const FOOD_TIMELINE = [
  {
    time: t("food.timeline_1_time"),
    title: t("food.timeline_1_title"),
    text: t("food.timeline_1_text"),
  },
  {
    time: t("food.timeline_2_time"),
    title: t("food.timeline_2_title"),
    text: t("food.timeline_2_text"),
  },
  {
    time: t("food.timeline_3_time"),
    title: t("food.timeline_3_title"),
    text: t("food.timeline_3_text"),
  },
  {
    time: t("food.timeline_4_time"),
    title: t("food.timeline_4_title"),
    text: t("food.timeline_4_text"),
  },
];

const GOOD_HABITS = [
  t("food.good_1"),
  t("food.good_2"),
  t("food.good_3"),
  t("food.good_4"),
  t("food.good_5"),
  t("food.good_6"),
];



  const [started, setStarted] = useState(false);
  const [active, setActive] = useState(FOOD_STEPS[0].id);

  const step = FOOD_STEPS.find((f) => f.id === active);

return (
  <div className="min-h-screen bg-gradient-to-b from-green-50 to-emerald-100 text-slate-800 flex flex-col">

    {/* HEADER */}
    <header className="w-full border-b border-slate-200 bg-white/70 backdrop-blur sticky top-0 z-10">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg md:text-2xl font-semibold">
          {t("food.header_title")}
        </h1>
        <span className="text-xs md:text-sm text-slate-500">
          {t("food.for_classes")}
        </span>
      </div>
    </header>

    {/* MAIN BODY */}
    <main className="flex-1 max-w-6xl mx-auto px-4 py-6 space-y-10">

      {/* INTRO */}
      {!started && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-2 gap-8 items-center"
        >
          {/* IMAGE */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
            className="flex justify-center"
          >
            <img
              src={FoodIntroImg}
              alt="Healthy food intro"
              className="w-60 h-60 md:w-72 md:h-72 object-cover rounded-3xl shadow-xl"
            />
          </motion.div>

          {/* TEXT */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold">
              {t("food.intro_title")}
            </h2>
            <p className="text-slate-600">
              {t("food.intro_text")}
            </p>

            <ul className="list-disc text-sm text-slate-600 ml-4 space-y-1">
              <li>{t("food.intro_point1")}</li>
              <li>{t("food.intro_point2")}</li>
              <li>{t("food.intro_point3")}</li>
            </ul>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setStarted(true)}
              className="mt-4 bg-emerald-600 text-white px-5 py-2.5 rounded-full shadow-md"
            >
              {t("food.start_btn")}
            </motion.button>
          </div>
        </motion.section>
      )}

      {/* LAB CONTENT */}
      {started && (
        <>
          {/* SECTION 1 — HEALTHY / UNHEALTHY / HYGIENE */}
          <section className="space-y-4">
            <h3 className="text-xl md:text-2xl font-semibold">
              {t("food.section1_title")}
            </h3>

            <div className="flex flex-wrap gap-2">
              {FOOD_STEPS.map((f) => {
                const selected = f.id === active;
                return (
                  <motion.button
                    key={f.id}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setActive(f.id)}
                    className={`px-4 py-1.5 rounded-full text-sm border ${
                      selected
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-700 border-slate-200"
                    }`}
                  >
                    {f.label}
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="grid md:grid-cols-3 gap-5"
              >
                {/* LEFT — IMAGE */}
                <div className="bg-white rounded-2xl shadow-sm p-4">
                  <p className="text-xs font-semibold text-emerald-600">
                    {t("food.visual_text")}
                  </p>

                  <div className="mt-3 h-48 rounded-2xl overflow-hidden bg-emerald-50 shadow">
                    <motion.img
                      key={step.id}
                      src={
                        step.id === "healthy"
                          ? HealthyImg
                          : step.id === "unhealthy"
                          ? UnhealthyImg
                          : HygieneImg
                      }
                      alt={step.title}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>

                {/* RIGHT — CONTENT */}
                <div className="md:col-span-2 bg-white rounded-2xl shadow-sm p-5">
                  <h4 className="text-lg font-semibold mb-1">{step.title}</h4>
                  <p className="text-sm text-slate-600">{step.description}</p>

                  <ul className="mt-3 list-disc ml-5 text-sm text-slate-700 space-y-1">
                    {step.items.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>

                  <div className="mt-3 text-sm italic text-slate-700 border-t pt-2">
                    {step.example}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </section>

          {/* SECTION 2 — TIMELINE */}
          <section className="space-y-4">
            <h3 className="text-xl md:text-2xl font-semibold">
              {t("food.section2_title")}
            </h3>

            <div className="relative pl-6">
              <div className="absolute left-1 top-0 w-px h-full bg-emerald-300"></div>

              <div className="space-y-4">
                {FOOD_TIMELINE.map((item, i) => (
                  <motion.div
                    key={item.time}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="relative pl-4"
                  >
                    <div className="absolute left-[-7px] top-2 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow"></div>

                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs font-semibold text-emerald-600">
                          {item.time}
                        </span>
                        <h4 className="text-sm font-semibold">{item.title}</h4>
                      </div>
                      <p className="text-xs md:text-sm text-slate-600 mt-1">
                        {item.text}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* SECTION 3 — GOOD HABITS */}
          <section className="space-y-4 mb-10">
            <h3 className="text-xl md:text-2xl font-semibold">
              {t("food.section3_title")}
            </h3>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {GOOD_HABITS.map((h, i) => (
                <motion.div
                  key={h}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.03 }}
                  className="bg-white rounded-xl shadow-sm p-4 border border-slate-100 flex items-start gap-2"
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1"></div>
                  <p className="text-sm text-slate-700">{h}</p>
                </motion.div>
              ))}
            </div>

          </section>
        </>
      )}
    </main>
  </div>
);

}
